import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { getTownAtTile, getVisibleTiles } from '../utils/hexWorldMapUtils';
import { useHexMapControls } from '../hooks/useHexMapControls';
import HexGrid from './HexGrid';
import TownDetailsModal from './TownDetailsModal';
import HexTileModal from './HexTileModal';
import './HexWorldMap.css';

export interface HexTile {
  id: number;
  x: number;
  y: number;
  tileType: string;
  biomeType: string;
  zoneLevel: number;
  name: string;
  description: string;
  requiredLevel: number;
  isStartingTile: boolean;
  allowsGathering: boolean;
  gatheringType?: string;
  allowsBattle: boolean;
  maxPlayerUnits: number;
  imageUrl?: string;
  iconName?: string;
  hexOffsetRow: number;
  isUnlocked: boolean;
  battlesWon: number;
  isCurrentLocation: boolean;
  hasResources: boolean;
  hasMonsters: boolean;
  highestSpawnDifficulty?: string;
  // Town properties for boss battles
  townId?: number;
  townName?: string;
  bossName?: string;
  townType?: string;
}

export interface TileDetails {
  tile: {
    id: number;
    name: string;
    description: string;
    tileType: string;
    biomeType: string;
    zoneLevel: number;
    allowsGathering: boolean;
    allowsBattle: boolean;
    maxPlayerUnits: number;
  };
  resources: Array<{
    id: number;
    resourceType: string;
    currentStock: number;
    maxStock: number;
    resourceRarity: number;
    rarityName: string;
    respawnTime: number;
    isAvailable: boolean;
  }>;
  monsters: Array<{
    id: number;
    name: string;
    level: number;
    type: string;
    imageUrl?: string;
    difficultyLevel: number;
    difficulty: string;
    spawnSlot: number;
    respawnTime: number;
    isAvailable: boolean;
    lastDefeatedAt?: string;
    minutesUntilRespawn?: number;
  }>;
  totalMonsterSpawns: number;
  totalResourceNodes: number;
}

interface HexWorldMapProps {
  onStartBattle?: (battleId: number) => void;
}

export interface Character {
  id: number;
  name: string;
  level: number;
  class: string;
  currentHP: number;
  maxHP: number;
  gold?: number;
}

export interface Town {
  id: number;
  name: string;
  description: string;
  townType: string;
  worldX: number;
  worldY: number;
  bossName: string;
  isLiberated: boolean;
  standing: number;
  level: number;
  lastActivityDate: string | null;
  canFastTravel: boolean;
}

export interface ActiveGathering {
  id: number;
  characterId: number;
  characterName: string;
  tileId: number;
  tileName: string;
  gatheringType: string;
  startTime: string;
  totalCollections: number;
  elapsedTime: string;
  pendingCollections: number;
}

const HexWorldMap: React.FC<HexWorldMapProps> = ({ onStartBattle }) => {
  const { handleAuthError } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial } = useTutorial();
  const [tiles, setTiles] = useState<HexTile[]>([]);
  const [currentTileId, setCurrentTileId] = useState<number>(0);
  const [travelPoints, setTravelPoints] = useState<number>(3);
  const [selectedTile, setSelectedTile] = useState<HexTile | null>(null);
  const [tileDetails, setTileDetails] = useState<TileDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [viewRadius, setViewRadius] = useState<number>(10); // Show tiles within 10 units of player
  const { zoom, panOffset, isPanning, setZoom, setPanOffset, handleMouseDown, handleMouseMove, handleMouseUp, handleWheel, onTouchStart, onTouchMove, onTouchEnd } = useHexMapControls();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<number[]>([]);
  const [selectedMonsterSpawn, setSelectedMonsterSpawn] = useState<number | null>(null);

  // Town state
  const [towns, setTowns] = useState<Town[]>([]);
  const [selectedTownId, setSelectedTownId] = useState<number | null>(null);

  // Gathering state
  const [activeGathering, setActiveGathering] = useState<ActiveGathering[]>([]);
  const [selectedGatherCharacterId, setSelectedGatherCharacterId] = useState<number | null>(null);

  const fetchWorldMap = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/worldmap/tiles');

      if (response.ok) {
        const data = await response.json();
        setTiles(data.tiles || []);
        setCurrentTileId(data.currentTileId);
        setTravelPoints(data.travelPoints);

        if (data.message) {
          setMessage(data.message);
        }
      } else {
        setError('Failed to fetch world map');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  const fetchCharacters = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/characters');
      if (response.ok) {
        const data = await response.json();
        setCharacters(data);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
      }
    }
  }, [handleAuthError]);

  const fetchTowns = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/town');
      if (response.ok) {
        const data = await response.json();
        setTowns(data);
      }
    } catch (err) {
      logger.error('Error fetching towns:', err);
    }
  }, []);

  const fetchTileDetails = useCallback(async (tileId: number) => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/worldmap/tiles/${tileId}/details`);

      if (response.ok) {
        const data = await response.json();
        setTileDetails(data);
      } else {
        setError('Failed to fetch tile details');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  // Calculate hex distance using axial coordinates
  const handleTravel = async (tileId: number) => {
    // Check if any character is currently gathering
    if (activeGathering.length > 0) {
      const gatheringNames = activeGathering.map(g => g.characterName).join(', ');
      addNotification({
        type: 'info',
        title: '⚠️ Characters Gathering',
        message: `${gatheringNames} is currently gathering. Stop gathering before traveling.`
      });
      setError(`Stop gathering first: ${gatheringNames}`);
      return;
    }

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/worldmap/travel/${tileId}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setTravelPoints(data.travelPoints);
        await fetchWorldMap(); // Refresh map
        setSelectedTile(null);
        setTileDetails(null);
        setMessage(`Traveled to ${data.newLocation.name}`);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to travel');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartBattle = async () => {
    if (!selectedTile || selectedCharacterIds.length === 0) {
      setError('Please select at least one character');
      return;
    }

    // Only require monster spawn selection for non-boss battles
    if (!selectedTile.bossName && selectedMonsterSpawn === null) {
      setError('Please select a monster spawn to battle');
      return;
    }

    if (!selectedTile.isCurrentLocation) {
      setError('You must travel to this tile first to start a battle');
      return;
    }

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/worldmap/tiles/${selectedTile.id}/battle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          characterIds: selectedCharacterIds,
          monsterSpawnId: selectedTile.bossName ? null : selectedMonsterSpawn  // Send null for boss battles
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(`Battle started! ${data.playerUnits} vs ${data.enemyUnits} enemies`);

        // Call the onStartBattle callback if provided
        if (onStartBattle && data.battleId) {
          onStartBattle(data.battleId);
        }

        handleCloseModal();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to start battle');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCharacterSelection = (characterId: number) => {
    setSelectedCharacterIds(prev => {
      if (prev.includes(characterId)) {
        return prev.filter(id => id !== characterId);
      } else {
        // Respect max player units from tile
        if (tileDetails && prev.length >= tileDetails.tile.maxPlayerUnits) {
          setError(`Maximum ${tileDetails.tile.maxPlayerUnits} characters allowed`);
          return prev;
        }
        return [...prev, characterId];
      }
    });
  };

  const handleTileClick = (tile: HexTile) => {
    const town = getTownAtTile(towns, tile.x, tile.y);

    if (town && town.isLiberated) {
      // Liberated town - show town modal (has quests if you're there, fast travel if not)
      setSelectedTownId(town.id);
      setSelectedTile(null); // Don't show tile modal
      setTileDetails(null); // Clear tile details
      setError(null);
      setSelectedCharacterIds([]); // Reset selection when opening new tile
      setSelectedMonsterSpawn(null); // Reset monster spawn selection
    } else {
      // Regular tile OR un-liberated town - show tile modal
      setSelectedTownId(null); // Don't show town modal
      setSelectedTile(tile);
      setError(null);
      setSelectedCharacterIds([]); // Reset selection when opening new tile
      setSelectedMonsterSpawn(null); // Reset monster spawn selection
      fetchTileDetails(tile.id);
    }
  };

  const handleCloseModal = () => {
    setSelectedTile(null);
    setTileDetails(null);
    setSelectedCharacterIds([]);
    setSelectedMonsterSpawn(null);
    setSelectedGatherCharacterId(null);
    setSelectedTownId(null); // Also close town modal if open
  };

  const fetchActiveGathering = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/gathering/active');
      if (response.ok) {
        const data = await response.json();
        setActiveGathering(data.activeGathering || []);

        for (const stopped of (data.autoStopped || [])) {
          const materialsText = stopped.materialsCollected?.length > 0
            ? stopped.materialsCollected.map((m: { quantity: number; materialName: string }) => `${m.quantity}x ${m.materialName}`).join(', ')
            : 'No materials';
          addNotification({
            type: 'loot',
            title: '⏰ Gathering Complete (24h limit)',
            message: `${stopped.characterName} gathered at ${stopped.tileName}: ${materialsText}`
          });
        }
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
      }
      // Silently fail for gathering fetch - not critical
    }
  }, [handleAuthError, addNotification]);

  const handleStartGathering = async () => {
    if (!selectedTile || !selectedGatherCharacterId) {
      setError('Please select a character to gather');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/gathering/start/${selectedGatherCharacterId}/${selectedTile.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        setMessage(`${data.characterName} started gathering ${data.gatheringType} at ${data.tileName}`);
        await fetchActiveGathering();
        setSelectedGatherCharacterId(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to start gathering');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopGathering = async (gatheringId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await makeAuthenticatedRequest(
        `/gathering/stop/${gatheringId}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();

        // Build materials list
        const materialsText = data.materialsCollected && data.materialsCollected.length > 0
          ? data.materialsCollected.map((m: { quantity: number; materialName: string }) => `${m.quantity}x ${m.materialName}`).join(', ')
          : 'No materials';

        const bonusText = data.bonuses ? ` (${data.bonuses.gatheringBonus} town bonus!)` : '';
        const fullMessage = `${data.characterName} gathered: ${materialsText}${bonusText}`;

        setMessage(fullMessage);

        // Show success notification with details
        addNotification({
          type: 'loot',
          title: '✅ Gathering Complete!',
          message: fullMessage
        });

        await fetchActiveGathering();
      } else {
        const errorData = await response.json();
        if (response.status === 409) {
          // Already claimed by a near-simultaneous request (e.g. double-click) - resync instead of erroring.
          await fetchActiveGathering();
        } else {
          setError(errorData.message || 'Failed to stop gathering');
        }
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFastTravel = async (townId: number) => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/town/${townId}/fast-travel`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fast travel');
      }

      const data = await response.json();
      setMessage(`${data.message} (TP Cost: ${data.tpCost}, Remaining: ${data.remainingTp})`);

      // Refresh map and close town modal
      await fetchWorldMap();
      setSelectedTownId(null);
      setError(null);
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorldMap();
    fetchCharacters();
    fetchTowns();
    fetchActiveGathering();
  }, [fetchWorldMap, fetchCharacters, fetchTowns, fetchActiveGathering]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Auto-trigger world map tutorial for first-time players
  useEffect(() => {
    if (tiles.length > 0 && !isLoading) {
      checkAndStartTutorial('world-map-basics');
    }
  }, [tiles, isLoading, checkAndStartTutorial]);

  // TP Regeneration polling - fetch every 30 seconds to trigger backend regeneration
  useEffect(() => {
    const tpInterval = setInterval(() => {
      fetchWorldMap(); // This calls /worldmap/tiles which triggers TP regeneration on backend
    }, 30000); // Every 30 seconds

    return () => clearInterval(tpInterval);
  }, [fetchWorldMap]);

  // Dynamic map dimensions — read from the container div via ResizeObserver
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 1660, height: 600 });

  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setMapDimensions({ width: el.clientWidth, height: el.clientHeight });
    });
    ro.observe(el);
    setMapDimensions({ width: el.clientWidth, height: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  return (
    <div className="hex-world-map">
      <div className="hex-world-header">
        <h2>🗺️ World Map</h2>
        <span className="world-description">Explore a vast procedural world filled with resources and monsters</span>
        <div className="travel-points">
          <span className="tp-label">TP</span>
          <span className="tp-value">{travelPoints}/10</span>
        </div>
        {activeGathering.length > 0 && (
          <div className="hwm-gathering-badge">
            ⚠ Gathering ({activeGathering.length})
          </div>
        )}
      </div>

      {message && (
        <div className="info-message">
          {message}
          {message.includes('not generated') && (
            <div style={{ marginTop: '10px' }}>
              <small>The admin needs to generate the hex world first.</small>
            </div>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Hex Grid */}
      {tiles.length > 0 ? (
        <>
          <div
            ref={mapContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{
              overflow: 'hidden',
              position: 'relative',
              border: '1px solid var(--mb)',
              borderTop: '2px solid var(--gold)',
              borderRadius: '3px',
              background: '#1a1a2e',
              flex: 1,
              minHeight: '400px',
              cursor: isPanning ? 'grabbing' : 'grab',
              touchAction: 'none'
            }}
          >
            <HexGrid
              tiles={getVisibleTiles(tiles, currentTileId, viewRadius)}
              currentTileId={currentTileId}
              onTileClick={handleTileClick}
              zoom={zoom}
              centerX={panOffset.x}
              centerY={panOffset.y}
              viewportWidth={mapDimensions.width}
              viewportHeight={mapDimensions.height}
              towns={towns}
            />
          </div>

          {/* Map Controls */}
          <div className="map-controls">
            <button className="hwm-ctrl-btn" onClick={() => setZoom(Math.max(0.5, zoom - 0.5))} disabled={zoom <= 0.5}>
              − Zoom Out
            </button>
            <span className="zoom-display">{Math.round(zoom * 100)}%</span>
            <button className="hwm-ctrl-btn" onClick={() => setZoom(Math.min(3, zoom + 0.5))} disabled={zoom >= 3}>
              + Zoom In
            </button>
            <button className="hwm-ctrl-btn" onClick={() => { setZoom(3); setPanOffset({ x: 0, y: 0 }); }}>
              Reset View
            </button>
            <button className="hwm-ctrl-btn" onClick={() => setViewRadius(viewRadius === 10 ? 999 : 10)} title={viewRadius === 10 ? 'Show full map' : 'Show nearby tiles only'}>
              {viewRadius === 10 ? 'Full Map' : 'Nearby'}
            </button>
            <span className="tile-count">
              {getVisibleTiles(tiles, currentTileId, viewRadius).length} / {tiles.length} tiles
            </span>
            <button className="hwm-ctrl-btn refresh-button" onClick={fetchWorldMap}>
              Refresh
            </button>
          </div>
        </>
      ) : (
        <div className="no-map-message">
          {isLoading ? 'Loading world map...' : 'No world map available'}
        </div>
      )}

      {/* Town Details Modal */}
      {selectedTownId && (
        <TownDetailsModal
          townId={selectedTownId}
          characters={characters}
          onClose={handleCloseModal}
          onFastTravel={handleFastTravel}
          onCharacterGoldChanged={fetchCharacters}
        />
      )}

      {/* Tile Details Modal */}
      {selectedTile && tileDetails && (
        <HexTileModal
          selectedTile={selectedTile}
          tileDetails={tileDetails}
          tiles={tiles}
          travelPoints={travelPoints}
          characters={characters}
          selectedCharacterIds={selectedCharacterIds}
          selectedMonsterSpawn={selectedMonsterSpawn}
          activeGathering={activeGathering}
          selectedGatherCharacterId={selectedGatherCharacterId}
          isLoading={isLoading}
          onClose={handleCloseModal}
          onTravel={handleTravel}
          onStartBattle={handleStartBattle}
          onToggleCharacter={toggleCharacterSelection}
          onSelectMonsterSpawn={setSelectedMonsterSpawn}
          onStartGathering={handleStartGathering}
          onStopGathering={handleStopGathering}
          onSelectGatherCharacter={setSelectedGatherCharacterId}
        />
      )}
    </div>
  );
};

export default HexWorldMap;
